<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useGameStore } from '@/stores/gameStore.ts'
import { useFleetStore } from '@/stores/fleetStore.ts'
import { useShipActionStore } from '@/stores/shipActionStore.ts'
import { useMapStore } from '@/stores/mapStore.ts'

/**
 * Props
 */
const props = defineProps({
  fleetIds: Array,
  smartPositioning: {
    type: Boolean,
    default: false
  }
})

/**
 * Stores / models
 */
const gameStore = useGameStore()
const fleetStore = useFleetStore()
const shipActionStore = useShipActionStore()
const mapStore = useMapStore()

/**
 * Variables
 */
const menuRef = ref(null)

/**
 * Functions
 */
function toggleMenu() {
  if (gameStore.pullDownMenuVisible === 'mapFleetMenu') {
    closeMenu()
  } else {
    gameStore.pullDownMenuVisible = 'mapFleetMenu'
  }
}

function closeMenu() {
  gameStore.pullDownMenuVisible = ''
}

const selectFleet = (fleetId) => {
  const fleet = fleetStore.findFleetById(fleetId)
  shipActionStore.initFleet(fleet)
  mapStore.disableAreaSelection()
  closeMenu()
}

function handleClickOutside(event) {
  if (!menuRef.value.contains(event.target)) {
    closeMenu()
  }
}

/**
 * Lifecycle hooks
 */
onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})

// Calculate dropdown position to keep it on screen
const dropdownPosition = computed(() => {
  if (!menuRef.value) return { top: '100%', left: '0' }

  // If smart positioning is disabled, always use default positioning
  if (!props.smartPositioning) {
    return { top: '100%', left: '0', transform: '' }
  }

  const menuWidth = 192 // w-48 = 192px
  const menuHeight = Math.max(160, props.fleetIds.length * 40) // Estimate height based on fleet count

  // Get map container dimensions
  const mapContainer = document.querySelector('.webgl-container')
  if (!mapContainer) return { top: '100%', left: '0' }

  const containerRect = mapContainer.getBoundingClientRect()
  const triggerRect = menuRef.value.parentElement?.getBoundingClientRect()

  if (!triggerRect) return { top: '100%', left: '0' }

  let top = '100%'
  let left = '0'
  let transform = ''

  // Check if dropdown would go off bottom of container
  if (triggerRect.bottom + menuHeight > containerRect.bottom) {
    // Flip to appear above, but still connected to the icon
    top = '0'
    transform = 'translateY(-100%)'
  }

  // Check if dropdown would go off right side of container
  if (triggerRect.left + menuWidth > containerRect.right) {
    // Align right edge of dropdown with right edge of trigger
    left = `-${menuWidth - triggerRect.width}px`
  }

  return { top, left, transform }
})
</script>

<template>
  <div class="relative" @click.stop="toggleMenu">
    <slot></slot>
    <!-- Dropdown Menu -->
    <div
      v-show="gameStore.pullDownMenuVisible === 'mapFleetMenu'"
      ref="menuRef"
      class="absolute w-48 border border-slate-500 bg-slate-800 rounded z-50"
      :style="{
        top: dropdownPosition.top,
        left: dropdownPosition.left,
        transform: dropdownPosition.transform
      }"
    >
      <template v-for="fleetId in fleetIds" :key="fleetId">
        <div
          class="p-2 flex items-center text-slate-300 hover:bg-slate-500"
          @click="selectFleet(fleetId)"
        >
          {{ fleetStore.getFleetNameById(fleetId) }}
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped>
/* Add any specific styles for your dropdown here */
</style>
